import { CSS } from "@dnd-kit/utilities"
import { useSortable } from "@dnd-kit/sortable"
import { SiteProps, CampingStyleProps } from "~/interfaces"
import { Bars4Icon, DocumentDuplicateIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline"
import Button from "~/components/ui/Button"
import SiteIcons from "~/components/SiteIcons"
import formatLengthUnit from "~/utils/formatLengthUnit"

interface Props {
  site: SiteProps
  campingStyle: CampingStyleProps
  lengthUnit: string
  onDuplicate: () => void
  onRemove: () => void
  onEdit: () => void
  actionsDisabled?: boolean
}

export default function SortableSite({
  site,
  campingStyle,
  lengthUnit,
  onDuplicate,
  onRemove,
  onEdit,
  actionsDisabled = false,
}: Props) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: site.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const featureItems = [
    site.electric && site.electric !== "none" ? (site.electric === "basic" ? "Electric" : `${site.electric}A`) : null,
    site.max_rig_length ? `${site.max_rig_length}${formatLengthUnit(lengthUnit)}` : null,
    site.minimum_capacity ? `${site.minimum_capacity} min` : null,
    site.camper_capacity ? `${site.camper_capacity} max` : null,
  ].filter(Boolean)

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg md:min-h-14 md:max-h-20 pl-2 pr-4 py-2 ml-4 flex justify-between items-center ${site.is_bookable ? "bg-white" : "bg-gray-150"}`}
      {...attributes}
    >
      {/* Desktop layout */}
      <div className="hidden md:flex items-center gap-3 w-full">
        <div className="w-10 flex justify-center">
          <Bars4Icon className="w-6 h-6 cursor-move" {...listeners} />
        </div>
        <div className="flex justify-start">
          <SiteIcons site={site} size="10" campingStyle={campingStyle} lengthUnit={lengthUnit} showLength={false} />
        </div>
        <div className="flex flex-col">
          <span>{site.name}</span>
          <div className="flex text-xs items-center gap-x-1 text-gray-500">
            {featureItems.map((item, index) => (
              <span key={index} className="flex items-center gap-x-1">
                {item}
                {index < featureItems.length - 1 && <div className="w-0.75 h-0.75 bg-gray-400 rounded-full" />}
              </span>
            ))}
          </div>
        </div>
      </div>
      {/* Mobile card layout */}
      <div className="flex flex-col md:hidden w-full bg-white p-2 ">
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center">
            <Bars4Icon className="w-5 h-5 cursor-move" {...listeners} />
            <span className="font-semibold">{site.name}</span>
          </div>
          <div className="flex gap-1">
            <Button
              variant="gray"
              size="small"
              onClick={onEdit}
              disabled={actionsDisabled}
              children={<PencilIcon className="w-4 h-4" />}
            />
            <Button
              variant="gray"
              size="small"
              onClick={onDuplicate}
              disabled={actionsDisabled}
              children={<DocumentDuplicateIcon className="w-4 h-4" />}
            />
            <Button
              variant="gray"
              size="small"
              onClick={onRemove}
              disabled={actionsDisabled}
              children={<TrashIcon className="w-4 h-4" />}
            />
          </div>
        </div>
        <div className="flex items-center justify-start gap-2 mt-4">
          <SiteIcons site={site} size="10" campingStyle={campingStyle} lengthUnit={lengthUnit} showLength={false} />
          <div className="flex flex-wrap items-center gap-x-2 text-gray-500">
            {featureItems.map((item, index) => (
              <span key={index} className="flex items-center gap-x-1">
                {item}
                {index < featureItems.length - 1 && <div className="w-0.75 h-0.75 bg-gray-400 rounded-full" />}
              </span>
            ))}
          </div>
        </div>
      </div>
      {/* Desktop action buttons */}
      <div className="hidden md:flex gap-2">
        <Button
          variant="gray"
          size="small"
          onClick={onEdit}
          disabled={actionsDisabled}
          icon={<PencilIcon className="w-4 h-4" />}
        >
          Edit
        </Button>
        <Button
          variant="gray"
          size="small"
          onClick={onDuplicate}
          disabled={actionsDisabled}
          icon={<DocumentDuplicateIcon className="w-4 h-4" />}
        >
          Duplicate
        </Button>
        <Button
          variant="gray"
          size="small"
          onClick={onRemove}
          disabled={actionsDisabled}
          icon={<TrashIcon className="w-4 h-4" />}
        >
          Delete
        </Button>
      </div>
    </li>
  )
}
